import { dropOrphanToolCalls } from '../runner/items';
import type { AgentInputItem } from '../types';
import { compactSummaryToModelMessage } from './prompt';
import { buildStableHistoryItems } from './refs';
import type {
  CompactBoundaryRecord,
  CompactionRecord,
  CompactSummaryRecord,
  TranscriptViewItem,
} from './types';

export function assembleTranscriptItems(
  historyItems: AgentInputItem[],
  compactionRecords: CompactionRecord[],
  options: {
    collapseCompactedRanges?: boolean;
    includeSupersededBoundaries?: boolean;
  } = {},
): TranscriptViewItem[] {
  const refs = buildStableHistoryItems(historyItems);
  const refById = new Map(refs.map((entry) => [entry.id, entry]));
  const summaries = getSummariesByBoundaryId(compactionRecords);
  const boundaries = getBoundaryRecords(compactionRecords).filter((boundary) =>
    options.includeSupersededBoundaries === false
      ? !isSuperseded(boundary, compactionRecords)
      : true,
  );
  const boundaryGroups = new Map<string, TranscriptViewItem[]>();
  for (const boundary of boundaries) {
    const summary = summaries.get(boundary.id);
    const group: TranscriptViewItem[] = [
      { ...boundary, virtual: true as const },
      ...(summary
        ? [{ ...summary, virtual: true as const } as TranscriptViewItem]
        : []),
    ];
    const existing = boundaryGroups.get(boundary.insertAfterItemId) ?? [];
    boundaryGroups.set(boundary.insertAfterItemId, [...existing, ...group]);
  }

  if (options.collapseCompactedRanges) {
    const active = getActiveBoundary(historyItems, compactionRecords);
    const summary = active ? summaries.get(active.id) : undefined;
    if (active && summary) {
      const from = refById.get(active.compactedRange.fromItemId)?.index;
      const to = refById.get(active.compactedRange.toItemId)?.index;
      if (typeof from === 'number' && typeof to === 'number' && from <= to) {
        return [
          ...historyItems.slice(0, from),
          { ...active, virtual: true as const },
          { ...summary, virtual: true as const },
          ...historyItems.slice(to + 1),
        ];
      }
    }
  }

  const output: TranscriptViewItem[] = [];
  for (const ref of refs) {
    output.push(ref.item);
    const group = boundaryGroups.get(ref.id);
    if (group) {
      output.push(...group);
    }
  }

  for (const [insertAfterItemId, group] of boundaryGroups) {
    if (!refById.has(insertAfterItemId)) {
      output.push(...group);
    }
  }
  return output;
}

export function assembleModelContext(
  historyItems: AgentInputItem[],
  compactionRecords: CompactionRecord[],
  options: {
    preserveSystemMessages?: boolean;
  } = {},
): AgentInputItem[] {
  const active = getActiveBoundary(historyItems, compactionRecords);
  if (!active) {
    return dropOrphanToolCalls([...historyItems]);
  }
  const summaries = getSummariesByBoundaryId(compactionRecords);
  const summary = summaries.get(active.id);
  if (!summary) {
    return dropOrphanToolCalls([...historyItems]);
  }

  const refs = buildStableHistoryItems(historyItems);
  const fromIndex = refs.find(
    (entry) => entry.id === active.compactedRange.fromItemId,
  )?.index;
  const toIndex = refs.find(
    (entry) => entry.id === active.compactedRange.toItemId,
  )?.index;
  if (
    typeof fromIndex !== 'number' ||
    typeof toIndex !== 'number' ||
    fromIndex > toIndex
  ) {
    return dropOrphanToolCalls([...historyItems]);
  }

  const preserveSystemMessages = options.preserveSystemMessages ?? true;
  const systemMessages = preserveSystemMessages
    ? historyItems.filter(isSystemMessage)
    : [];
  const tail = historyItems
    .slice(toIndex + 1)
    .filter((item) => !(preserveSystemMessages && isSystemMessage(item)));
  return dropOrphanToolCalls([
    ...systemMessages,
    compactSummaryToModelMessage(summary.content),
    ...tail,
  ]);
}

function getActiveBoundary(
  historyItems: AgentInputItem[],
  records: CompactionRecord[],
): CompactBoundaryRecord | undefined {
  const refs = buildStableHistoryItems(historyItems);
  const refIds = new Set(refs.map((entry) => entry.id));
  const summaries = getSummariesByBoundaryId(records);
  const superseded = new Set(
    getBoundaryRecords(records).flatMap((boundary) =>
      boundary.supersedesBoundaryId ? [boundary.supersedesBoundaryId] : [],
    ),
  );

  return [...getBoundaryRecords(records)]
    .reverse()
    .find(
      (boundary) =>
        !superseded.has(boundary.id) &&
        summaries.has(boundary.id) &&
        refIds.has(boundary.compactedRange.fromItemId) &&
        refIds.has(boundary.compactedRange.toItemId),
    );
}

function getBoundaryRecords(
  records: CompactionRecord[],
): CompactBoundaryRecord[] {
  return records.filter(
    (record): record is CompactBoundaryRecord =>
      record.type === 'compact_boundary' && record.status === 'completed',
  );
}

function getSummariesByBoundaryId(
  records: CompactionRecord[],
): Map<string, CompactSummaryRecord> {
  const summaries = new Map<string, CompactSummaryRecord>();
  for (const record of records) {
    if (record.type === 'compact_summary') {
      summaries.set(record.boundaryId, record);
    }
  }
  return summaries;
}

function isSuperseded(
  boundary: CompactBoundaryRecord,
  records: CompactionRecord[],
): boolean {
  return getBoundaryRecords(records).some(
    (candidate) => candidate.supersedesBoundaryId === boundary.id,
  );
}

function isSystemMessage(item: AgentInputItem): boolean {
  return (
    item &&
    typeof item === 'object' &&
    item.type === 'message' &&
    item.role === 'system'
  );
}
